// betreffPraefix.ts — Antwort-/Weiterleitungsmarken am Anfang eines
// Mail-Betreffs (`Re:`, `AW:`, `WG:`, `FW:`, `Fwd:`, auch mehrfach gestapelt).
//
// Warum eine eigene Util statt `components/mail/betreffAusfuellen.ts`:
// Der Vorgang-Erstell-Flow (Cluster VORGANG, E5) übernimmt den Mail-Betreff und
// braucht dafür genau diese Regel. `components/vorgang/**` soll aber nicht in
// `components/mail/**` hineingreifen — deshalb liegt die reine Funktion hier,
// und `betreffAusfuellen.ts` re-exportiert sie unverändert weiter (Composer und
// BetreffPicker bleiben dadurch unangetastet).

/**
 * Reply-/Forward-Marken am Zeichenketten-Anfang.
 *
 * Deckt ab: `Re:`, `AW:`, `Antw:`, `Fw:`/`Fwd:`, `WG:`, `TR:`, `RV:` — mit und
 * ohne Leerzeichen, mit Outlook-Zähler (`AW[2]:`) und beliebig gestapelt.
 *
 * Zwei bewusst NICHT abgedeckte Randfälle (dokumentiert, kein Auftrag): das
 * ausgeschriebene „Antwort:" greift nicht (die Alternative `antw` verlangt
 * direkt danach den Doppelpunkt), und ein vorangestellter Fremdmarker wie
 * `[EXTERN] AW: …` blockiert das Abschneiden, weil die Regex am Anfang ankert.
 */
const PRAEFIX_RE = /^((?:\s*(?:re|aw|antw|fwd?|wg|tr|rv)\s*(?:\[\d+\])?\s*:\s*)+)/i;

/**
 * Zerlegt einen Betreff in das Antwort-Präfix und den Rest.
 *
 * Im Composer bleibt das Präfix ausdrücklich stehen: geht es verloren, reißt
 * der Thread in Outlook.
 */
export function trenneAntwortPraefix(subject: string | null | undefined): {
    praefix: string;
    rest: string;
} {
    const s = (subject ?? "").trim();
    const m = PRAEFIX_RE.exec(s);
    if (!m) return { praefix: "", rest: s };
    return { praefix: m[1].trim() + " ", rest: s.slice(m[1].length).trim() };
}

/**
 * E6 / Entscheidung E04: Betreff für die Übernahme in einen Vorgang — es wird
 * AUSSCHLIESSLICH das Antwort-Präfix entfernt.
 *
 * Das Courté-Betreffschema („Acme GmbH - VSNR 4711 / Halle 2 / Sturmschaden")
 * bleibt VOLLSTÄNDIG stehen; die früher geplante Anforderung E7 („Courté-Kopf
 * abschneiden") ist gestrichen. Deshalb wird `freierBetreffTeil` hier bewusst
 * NICHT verwendet.
 *
 * Bleibt nach dem Abschneiden nichts übrig (Betreff war z. B. nur „AW:"),
 * liefert die Funktion einen Leerstring — der Aufrufer entscheidet dann, ob er
 * das Feld unangetastet lässt (siehe Annahme A9 im Cluster VORGANG).
 */
export function betreffOhneAntwortPraefix(subject: string | null | undefined): string {
    return trenneAntwortPraefix(subject).rest;
}
